import {
    DetailsHeader, DetailsList, DetailsListLayoutMode,
    IColumn,
    IContextualMenuItem,
    IDetailsHeaderProps,
    IGroup,
    IRenderFunction, Selection, SelectionMode
} from "@fluentui/react";
import { RootState } from "app/store";
import CustomPanel from "components/controls/CustomPanel";
import { addUpdateGroupStructure } from "global/Reducer/dataSlice";
import { ArrayToArrayLookupByPredicate, copyPropertiesOnly } from "global/Utility/TSFunctions";
import { useMemo, useState } from "react";
import { ConnectedProps, connect, useDispatch } from "react-redux";
import { v4 as uuidv4 } from 'uuid';
import {
    IDepartment as FAM_IDepartment,
    IGroupConfigurationData as FAM_IGroupConfigurationData
} from "../../model/FlexibleAttributeModel";
import GroupStructureDepartmentEditor from "./GroupStructureDepartmentEditor";

import { SimpleGridStyles } from "../../components/layout/StickyDetailList";
import { useApplication } from "global/Application/Context";

export interface IDetailsListGroupedExampleItem {
    key: string;
    name: string;
    color: string;
}

export interface IDetailsListGroupedExampleState {
    items: IDetailsListGroupedExampleItem[];
    groups: IGroup[];
    showItemIndexInView: boolean;
    isCompactMode: boolean;
}

interface IProps extends TComponentConnectedProps {
    snap?: FAM_IGroupConfigurationData;
    snap2?: FAM_IGroupConfigurationData;
    compare?: boolean;
    disabled?: boolean;
}

interface IDataSource {
    items: any;
    groups: IGroup[]
}

interface IGridItem {
    key: string;
    cesNumber: string | null;
    departmentName: string | null;
    departmentDescription: string | null;
    data: FAM_IDepartment;
}

const GroupStructureDepartmentView = (props: IProps) => {
    const [selectedDepartment, setSelectedDepartment] = useState<FAM_IDepartment | null>(null)
    const [canEdit, setCanEdit] = useState(false);

    const application = useApplication();
    const dispatch = useDispatch();

    const snap = props.snap || props.state.data.json;

    const dataSource = useMemo(() => {

        const groups: IGroup[] = [];
        const items: IGridItem[] = [];

        const departmentsByCESNumber = ArrayToArrayLookupByPredicate(props.state.data.json.groupStructure.departments, t => t.cesNumberRef?.name ?? "");

        for (const cesNumber in departmentsByCESNumber) {
            const departments = departmentsByCESNumber[cesNumber];
            const startIndex = items.length;

            departments.forEach(eachDepartment => {
                // skip deactivated records/only show deactivated records if permitted at group config properties level
                if (eachDepartment.deactivated !== props.state.data.json.showInActives?.value)
                    return;

                items.push({
                    key: eachDepartment.id,
                    cesNumber: eachDepartment.cesNumberRef?.name!,
                    departmentName: eachDepartment.name,
                    departmentDescription: eachDepartment.description,
                    data: eachDepartment
                });

            });

            const count = items.length - startIndex;
            groups.push({ key: cesNumber, name: cesNumber, startIndex: startIndex, count: count });

        }

        const ds: IDataSource = {
            items: items,
            groups: groups
        }

        return ds;
    }, [
        snap.groupStructure?.groupNumbers,
        snap.groupStructure?.departments,
        props.snap2,
        props.compare,
        props.state.data.json.showInActives
    ]);

    const columns = useMemo(() => {
        const columns: IColumn[] = [
            { key: 'cesClientNumber', name: 'CES Client Number', fieldName: 'cesNumber', minWidth: 120, maxWidth: 140, isResizable: true },
            { key: 'department', name: 'Department', fieldName: 'departmentName', minWidth: 100, maxWidth: 120, isResizable: true },
            { key: 'departmentDescription', name: 'Description', fieldName: 'departmentDescription', minWidth: 150, maxWidth: 280, isResizable: true },
        ]
        return columns;
    }, []);

    const _onRenderDetailsHeader = (props?: IDetailsHeaderProps, defaultRender?: IRenderFunction<IDetailsHeaderProps>) => {
        return <DetailsHeader layoutMode={DetailsListLayoutMode.fixedColumns} {...props} />;
    }

    // const _onRenderColumn = (item: IDetailsListGroupedExampleItem, index?: number, column?: IColumn) => {
    //     const value =
    //         item && column && column.fieldName ? item[column.fieldName as keyof IDetailsListGroupedExampleItem] || '' : '';

    //     return <div data-is-focusable={true}>{value}</div>;
    // }


    const menu: IContextualMenuItem[] = [];
    if (!props.disabled && application.ChangeTypePermissions.groupStructure.department.add)
        menu.push({
            key: `gsDepartmentPageAdd`,
            name: 'Add',
            icon: 'PageAdd',
            onClick: (event, item) => {
                if (event?.stopPropagation)
                    event.stopPropagation();

                setSelectedDepartment({
                    id: uuidv4(),
                    name: null,
                    description: null,
                    cesNumberRef: null,
                    deactivated: false,
                    fields: [],
                })
            }
        });

    if (!props.disabled && application.ChangeTypePermissions.groupStructure.department.modify)
        if (canEdit)
            menu.push({
                key: `groupNumberPageEdit`,
                name: 'Edit',
                icon: 'PageEdit',
                onClick: (event, item) => {
                    if (event?.stopPropagation)
                        event.stopPropagation();

                    const selectedItems = selection.getSelection() as IGridItem[];
                    onSelect(selectedItems.map(q => q.data));
                },
            });

    const [selectedItems, setSelectedItems] = useState<FAM_IDepartment[] | null>(null);
    const selection = useMemo(() => {
        return new Selection({
            onSelectionChanged: () => {
                if (selection.getSelectedCount() > 0)
                    setCanEdit(true)
                else
                    setCanEdit(false)
            },
            selectionMode: SelectionMode.multiple,
        })
    }, []);

    const onSelect = (items: FAM_IDepartment[]) => {
        if (items) {
            setSelectedItems(items);

            if (items.length === 1) {
                // single items selected for edit?
                // lets only pick that single item and display for edit
                const item = items[0];
                setSelectedDepartment(structuredClone(item));

            } else if (items.length > 1) {

                // multiple items selected, lets create a a new blank template and use that for batch edit
                // later what ever changes that are done to this model we will cascade to selected items.
                const groupNumber: FAM_IDepartment = {
                    id: 'bulk',
                    name: null,
                    description: null,
                    cesNumberRef: null,
                    deactivated: false,
                    fields: [],
                };
                setSelectedDepartment(groupNumber);
            }
        }
    }




    return (
        <CustomPanel title={"Department"}
            storageId={"groupConfiguration.groupStructure.department"}
            canCollapse={true}
            defaultCollapsed={true}
            items={menu}
        >
            {selectedDepartment &&
                <GroupStructureDepartmentEditor
                    isOpen={!!selectedDepartment}
                    department={selectedDepartment}
                    disabled={props.disabled}
                    onConfirm={(department) => {
                        if (department.id === 'bulk') {

                            // bulk editing selected items?
                            const clones = structuredClone(selectedItems) ?? [];

                            clones.forEach(eClone => {
                                copyPropertiesOnly(department, eClone, ['cesNumberRef', 'description', 'name'], false, (key, source) => {
                                    if (!source[key])
                                        return false;
                                    return true;
                                });
                            })

                            dispatch(addUpdateGroupStructure({ departments: clones }))

                        } else {

                            //editing only single item?
                            dispatch(addUpdateGroupStructure({ departments: [department] }))
                        }
                    }}
                    onCancel={() => setSelectedDepartment(null)}
                    onDismiss={() => setSelectedDepartment(null)}
                />
            }
            <div style={{ minHeight: 15 }}></div>
            <DetailsList
                items={dataSource.items}
                groups={dataSource.groups}
                columns={columns}
                styles={SimpleGridStyles}
                selection={selection}
                getKey={(item, index) => { return item.key }}
                onRenderDetailsHeader={_onRenderDetailsHeader}
                layoutMode={DetailsListLayoutMode.fixedColumns}
                groupProps={{
                    showEmptyGroups: true,
                }}
                compact={true}
                selectionMode={SelectionMode.multiple}
                onItemInvoked={(item: IGridItem, index, event) => {

                    // to wait for render cycle to kick in first
                    // to open the right item, click was not passing in the right selected item.
                    setTimeout(() => {
                        const selectedItems = selection.getSelection() as IGridItem[];
                        onSelect(selectedItems.map(q => q.data));
                    }, 60);
                }}
            />
            <div style={{ minHeight: 15 }}></div>
        </CustomPanel>
    )
}


function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(GroupStructureDepartmentView);

